import axios from 'axios';
import { differenceInDays, isAfter } from 'date-fns';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import type { IParsedAgreementsCollection, ITableDataFields } from '../pages/homepage';

export async function getId() {
	return axios.get('/api/user/id');
}

export async function getIncompleteAgreements(userId: string) {
	return axios.get(`/api/user/${userId}/incompleteagreement`);
}

export async function getFleetCompanyForm(userId: string, fleetCompanyId: string) {
	return axios.get(`/api/user/${userId}/fleet/${fleetCompanyId}/form`);
}

export async function getAgreementsForUser(userId: string) {
	return axios.get(`/api/user/${userId}/agreement`);
}

export async function getNewAgreementForm() {
	return axios.get('/api/user/form/newagreement');
}

export async function getContactForm() {
	return axios.get('/api/user/form/contact');
}

export async function getIdentity() {
	return axios.get('/api/user/identity');
}

export const useFleetCompanyForm = (userId: string | number | undefined, fleetCompanyId: string | number | undefined) => {
	const [data, setData] = useState<any>({});
	const [isComplete, setIsComplete] = useState(false);

	useEffect(() => {
		if (userId === undefined || userId === null || fleetCompanyId === undefined || fleetCompanyId === null) {
			setIsComplete(true);
			return;
		}

		const result = async () =>
			getFleetCompanyForm(userId.toString(), fleetCompanyId.toString()).then(
                res => {
					setData(res.data);
					setIsComplete(true);
				},
				() => setIsComplete(true)
			);

		result();
	}, [userId, fleetCompanyId]);

	return { fleetCompanyForm: { data: data, isComplete: isComplete } };
};

export const useNewAgreementValues = (initialValues?: any) => {
	const [data, setData] = useState(initialValues || {});
	const [isComplete, setIsComplete] = useState(false);

	useEffect(() => {
		const result = async () =>
			getNewAgreementForm().then(
				res => {
					if (initialValues !== undefined) {
						setData((previousState: any) =>
							produce(previousState, (draftState: any) => {
								draftState.name = res.data.name;
								draftState.zip = res.data.zip;
								draftState.representativeEmail = res.data.representativeEmail;
							})
						);
					} else {
						setData(res.data);
					}
					setIsComplete(true);
				},
				() => {
					setIsComplete(true);
				}
			);

		result();
	}, [initialValues]);

	return { newAgreement: { data: data, isComplete: isComplete } };
};

export const useContactFormValues = (initialValues?: any) => {
	const [data, setData] = useState(initialValues || {});
	const [isComplete, setIsComplete] = useState(false);

	useEffect(() => {
		const result = async () =>
			getContactForm().then(
				res => {
					if (initialValues !== undefined) {
						setData((previousState: any) =>
							produce(previousState, (draftState: any) => {
								draftState.firstName = res.data.firstName;
								draftState.lastName = res.data.lastName;
								draftState.emailAddress = res.data.emailAddress;
								draftState.confirmEmailAddress = res.data.confirmEmailAddress;
							})
						);
					} else {
						setData(res.data);
					}
					setIsComplete(true);
				},
				() => setIsComplete(true)
			);

		result();
	}, [initialValues]);

	return { contactForm: { data: data, isComplete: isComplete } };
};

export const useIdentity = () => {
	const [data, setData] = useState({ id: 0, roleId: 0, firstName: '', lastName: '' });
	const [isComplete, setIsComplete] = useState(false);

	useEffect(() => {
		getIdentity().then(
			res => {
				setData(res.data);
				setIsComplete(true);
			},
			() => setIsComplete(true)
		);
	}, []);

	return { identity: { data: data, isComplete: isComplete } };
};

export const useUserAgreements = () => {
	const [agreements, setAgreements] = useState<IParsedAgreementsCollection>(parseAgreements([]));
	const [isComplete, setIsComplete] = useState(false);
	useEffect(() => {
		const result = async () => {
			await getId()
				.then(
					res => res.data.toString(),
					err => Promise.reject(err)
				)
				.then(
					res => {
						getAgreementsForUser(res).then(
							r => {
								setAgreements(parseAgreements(r.data.items));
								setIsComplete(true);
							},
							err => Promise.reject(err)
						);
					},
					e => Promise.reject(e)
				);
		};
		result();
	}, []);

	return { userAgreements: { data: agreements, isComplete: isComplete } };
};

const parseAgreements = (incomingData: any): IParsedAgreementsCollection => {
	const current: ITableDataFields[] = [];
	const past: ITableDataFields[] = [];
	const alerts: ITableDataFields[] = [];
	incomingData.forEach((agreement: ITableDataFields) => {
		const hasNotExpiredYet = isAfter(new Date(agreement.expirationDate), new Date());
		if (hasNotExpiredYet) {
			current.push(agreement);
			const alertNeeded =
				incomingData
					.filter((v: any) => (v as any).tspProvider === (agreement as any).tspProvider)
					.filter(
						(v: any) =>
							isAfter(new Date(v.expirationDate), new Date()) &&
							differenceInDays(new Date(agreement.expirationDate), new Date()) <= 60
					).length === 1;
			if (alertNeeded) {
				alerts.push(agreement);
			}
		} else {
			const notSignedYet = agreement.signedDate === null;
			notSignedYet ? current.push(agreement) : past.push(agreement);
		}
	});

	return {
		expiring: { title: `Alerts`, data: alerts },
		current: { title: `Current Agreements`, data: current },
		past: { title: `Past Agreements`, data: past },
	};
};

export const UserService = {
    getIncompleteAgreements,
	getFleetCompanyForm,
	getAgreementsForUser,
	useUserAgreements,
	useFleetCompanyForm,
	getNewAgreementForm,
	getContactForm,
	getIdentity,
	useNewAgreementValues,
	useContactFormValues,
	useIdentity,
};
